import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './ChatbotSettings.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatbotPreview from './ChatbotPreview';
import {
  FaPlus, FaTrash, FaCopy, FaSpinner, FaSave,
  FaPalette, FaCommentDots, FaCircle, FaCode, FaCheck,
  FaChevronDown, FaChevronRight, FaEye, FaEyeSlash,
  FaUpload, FaTimes, FaIdBadge, FaRocket, FaExclamationTriangle,
} from 'react-icons/fa';

const DEFAULT_SETTINGS = {
  primary_color: '#1F2937',
  secondary_color: '#FFFFFF',
  tertiary_color: '#F8F9FA',
  company_name: 'Your Company',
  agent_name: 'ChatBot',
  greetings_message: 'Bonjour ! Comment puis-je vous aider ?',
  brand_id: null,
  displayed: true,
  bubble_shape: 'circle',
  bubble_size: 60,
  bubble_position: 'bottom_right',
  adaptive_contrast: false,
  bubble_border_color: null,
  bubble_shadow: 'default',
  // v2 (2026-10-06)
  logo_key: null,
  subtitle: '',
  suggested_questions: [],
  teaser_enabled: false,
  teaser_message: '',
  teaser_delay_seconds: 6,
  welcome_title: '',
  header_color: null,
  language: 'fr',
  powered_by: true,
};

// Curated brand palettes: one click sets primary + header, white bubbles.
const THEME_PRESETS = ['#C47A4A', '#3FBDB4', '#4A6CC4', '#1F2937', '#B04A5A', '#3E7D52'];

// WCAG contrast of the brand color against its auto-picked text color
// (the widget chooses black or white text the same way).
const contrastRatio = (hex) => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const channel = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const r = channel(parseInt(hex.slice(1, 3), 16));
  const g = channel(parseInt(hex.slice(3, 5), 16));
  const b = channel(parseInt(hex.slice(5, 7), 16));
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const yiq = (parseInt(hex.slice(1, 3), 16) * 299 + parseInt(hex.slice(3, 5), 16) * 587 + parseInt(hex.slice(5, 7), 16) * 114) / 1000;
  const textL = yiq >= 128 ? 0 : 1; // black or white text
  const lighter = Math.max(L, textL);
  const darker = Math.min(L, textL);
  return (lighter + 0.05) / (darker + 0.05);
};

const ChatbotSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [chatbotsList, setChatbotsList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedChatbotId, setSelectedChatbotId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [previewMode, setPreviewMode] = useState('home');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null); // presigned, display only
  const [questionDraft, setQuestionDraft] = useState('');
  const logoInputRef = useRef(null);

  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    theme: true,
    welcome: false,
    bubble: false,
    deploy: false,
  });

  const [editSettings, setEditSettings] = useState({ ...DEFAULT_SETTINGS });

  const normalizeChatbot = (chatbot) => {
    if (!chatbot) return chatbot;
    return {
      ...chatbot,
      displayed: chatbot.displayed ?? true,
      bubble_shape: chatbot.bubble_shape || 'circle',
      bubble_size: chatbot.bubble_size || 60,
      bubble_position: chatbot.bubble_position || 'bottom_right',
      adaptive_contrast: chatbot.adaptive_contrast ?? false,
      bubble_border_color: chatbot.bubble_border_color || null,
      bubble_shadow: chatbot.bubble_shadow || 'default',
    };
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchInitialData(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchInitialData = async (token) => {
    try {
      setIsLoading(true);
      const userData = await fetchUserData(token);
      await fetchChatbotSettings(token);
      if (userData?.has_brand) {
        await fetchBrands(token);
      }
    } catch (err) {
      console.error('Failed to fetch initial data:', err);
      setError(t('chatbotSettings.loadError', 'Failed to load chatbot settings'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserData = async (token) => {
    const response = await axios.get('/api/users/user', {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser(response.data);
    return response.data;
  };

  const fetchChatbotSettings = async (token) => {
    const response = await axios.get('/api/chatbot/settings', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const normalizedList = (response.data || []).map(normalizeChatbot);
    setChatbotsList(normalizedList);
    if (normalizedList.length > 0) {
      const first = normalizedList[0];
      setSelectedChatbotId(first.id);
      setEditSettings(extractSettings(first));
      setLogoUrl(first.logo_url || null);
    }
  };

  const fetchBrands = async (token) => {
    try {
      const response = await axios.get('/api/brands/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(response.data || []);
    } catch {
      setBrands([]);
    }
  };

  const extractSettings = (chatbot) => ({
    primary_color: chatbot.primary_color,
    secondary_color: chatbot.secondary_color,
    tertiary_color: chatbot.tertiary_color,
    company_name: chatbot.company_name,
    agent_name: chatbot.agent_name,
    greetings_message: chatbot.greetings_message,
    brand_id: chatbot.brand_id,
    displayed: chatbot.displayed ?? true,
    bubble_shape: chatbot.bubble_shape || 'circle',
    bubble_size: chatbot.bubble_size || 60,
    bubble_position: chatbot.bubble_position || 'bottom_right',
    adaptive_contrast: chatbot.adaptive_contrast ?? false,
    bubble_border_color: chatbot.bubble_border_color || null,
    bubble_shadow: chatbot.bubble_shadow || 'default',
    logo_key: chatbot.logo_key || null,
    subtitle: chatbot.subtitle || '',
    suggested_questions: chatbot.suggested_questions || [],
    teaser_enabled: chatbot.teaser_enabled ?? false,
    teaser_message: chatbot.teaser_message || '',
    teaser_delay_seconds: chatbot.teaser_delay_seconds || 6,
    welcome_title: chatbot.welcome_title || '',
    header_color: chatbot.header_color || null,
    language: chatbot.language || 'fr',
    powered_by: chatbot.powered_by ?? true,
  });

  const selectedChatbot = chatbotsList.find((c) => c.id === selectedChatbotId);

  const handleSelectChatbot = (chatbot) => {
    if (hasUnsavedChanges) {
      if (!window.confirm(t('chatbotSettings.unsavedWarning', 'You have unsaved changes. Discard them?'))) {
        return;
      }
    }
    setSelectedChatbotId(chatbot.id);
    setEditSettings(extractSettings(chatbot));
    setLogoUrl(chatbot.logo_url || null);
    setHasUnsavedChanges(false);
    setError(null);
    setSuccess(null);
    setShowEmbedCode(false);
  };

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (name === 'bubble_size' || name === 'teaser_delay_seconds') {
      newValue = parseInt(value, 10) || (name === 'bubble_size' ? 60 : 6);
    } else if (name === 'brand_id') {
      newValue = value === '' ? null : value;
    } else {
      newValue = value;
    }
    setEditSettings((prev) => ({ ...prev, [name]: newValue }));
    setHasUnsavedChanges(true);
  }, []);

  const applyPreset = (color) => {
    setEditSettings((prev) => ({
      ...prev,
      primary_color: color,
      header_color: color,
      secondary_color: '#FFFFFF',
      tertiary_color: '#F8F9FA',
    }));
    setHasUnsavedChanges(true);
  };

  const handleLogoFile = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post('/api/chatbot/logo', form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setEditSettings((prev) => ({ ...prev, logo_key: response.data.logo_key }));
      setLogoUrl(response.data.logo_url);
      setHasUnsavedChanges(true);
    } catch (err) {
      console.error('Logo upload failed:', err);
      setError(err.response?.data?.detail || t('chatbotSettings.logoError', 'Logo upload failed (PNG, JPEG or WebP, 2 MB max).'));
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = () => {
    setEditSettings((prev) => ({ ...prev, logo_key: null }));
    setLogoUrl(null);
    setHasUnsavedChanges(true);
  };

  const addSuggestedQuestion = () => {
    const question = questionDraft.trim();
    if (!question) return;
    setEditSettings((prev) => {
      if ((prev.suggested_questions || []).length >= 4) return prev;
      return { ...prev, suggested_questions: [...(prev.suggested_questions || []), question.slice(0, 80)] };
    });
    setQuestionDraft('');
    setHasUnsavedChanges(true);
  };

  const removeSuggestedQuestion = (index) => {
    setEditSettings((prev) => ({
      ...prev,
      suggested_questions: (prev.suggested_questions || []).filter((_, i) => i !== index),
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    const token = localStorage.getItem('access_token');

    try {
      if (selectedChatbotId) {
        await axios.put(`/api/chatbot/settings/${selectedChatbotId}`, editSettings, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess(t('chatbotSettings.updateSuccess', 'Settings saved successfully'));
      } else {
        const response = await axios.post('/api/chatbot/settings', editSettings, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess(t('chatbotSettings.createSuccess', 'Chatbot created successfully'));
        if (response.data) {
          setSelectedChatbotId(response.data.id);
        }
      }
      await fetchChatbotSettings(token);
      setHasUnsavedChanges(false);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.detail || t('chatbotSettings.saveError', 'Failed to save settings'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateNew = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm(t('chatbotSettings.unsavedWarning', 'You have unsaved changes. Discard them?'))) {
        return;
      }
    }
    setSelectedChatbotId(null);
    setEditSettings({ ...DEFAULT_SETTINGS });
    setLogoUrl(null);
    setHasUnsavedChanges(false);
    setError(null);
    setSuccess(null);
    setShowEmbedCode(false);
  };

  const handleDelete = async (chatbotId) => {
    if (!window.confirm(t('chatbotSettings.confirmDelete', 'Are you sure you want to delete this chatbot?'))) {
      return;
    }
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`/api/chatbot/settings/${chatbotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchChatbotSettings(token);
      if (selectedChatbotId === chatbotId) {
        const remaining = chatbotsList.filter((c) => c.id !== chatbotId);
        if (remaining.length > 0) {
          setSelectedChatbotId(remaining[0].id);
          setEditSettings(extractSettings(remaining[0]));
          setLogoUrl(remaining[0].logo_url || null);
        } else {
          setSelectedChatbotId(null);
          setEditSettings({ ...DEFAULT_SETTINGS });
          setLogoUrl(null);
        }
      }
      setSuccess(t('chatbotSettings.deleteSuccess', 'Chatbot deleted successfully'));
      setHasUnsavedChanges(false);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Failed to delete chatbot:', err);
      setError(t('chatbotSettings.deleteError', 'Failed to delete chatbot'));
    }
  };

  const handleCopyEmbedCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = editSettings.brand_id ? `?brand_id=${editSettings.brand_id}` : '';
      const response = await axios.get(`/api/chatbot/embed${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const code = response.data.embed_code;
      setEmbedCode(code);
      setShowEmbedCode(true);
      setExpandedSections((prev) => ({ ...prev, deploy: true }));
      navigator.clipboard.writeText(code);
      setSuccess(t('chatbotSettings.copySuccess', 'Embed code copied to clipboard'));
      setTimeout(() => setSuccess(null), 4000);
    } catch {
      setError(t('chatbotSettings.embedError', 'Failed to generate embed code'));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const getBrandName = (brandId) => {
    if (!brandId) return t('chatbotSettings.defaultChatbot', 'Default Chatbot');
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : t('chatbotSettings.unknownBrand', 'Unknown Brand');
  };

  const renderSectionHeader = (key, icon, label) => (
    <button className="cs-section-header" onClick={() => toggleSection(key)}>
      <div className="cs-section-header-left">
        <span className="cs-section-icon">{icon}</span>
        <span>{label}</span>
      </div>
      {expandedSections[key] ? <FaChevronDown /> : <FaChevronRight />}
    </button>
  );

  const renderColorField = (name, label) => (
    <div className="cs-color-row" key={name}>
      <label htmlFor={name}>{label}</label>
      <div className="cs-color-input-group">
        <input
          type="color"
          id={name}
          name={name}
          value={editSettings[name] || editSettings.primary_color}
          onChange={handleChange}
          className="cs-color-picker"
        />
        <input
          type="text"
          value={editSettings[name] || ''}
          name={name}
          onChange={handleChange}
          className="cs-color-hex"
          placeholder={name === 'header_color' ? 'auto' : '#000000'}
          maxLength={7}
        />
      </div>
    </div>
  );

  const ratio = contrastRatio(editSettings.primary_color);
  const contrastOk = ratio !== null && ratio >= 4.5;

  if (isLoading) {
    return (
      <div className="cs-loading">
        <FaSpinner className="cs-spinner" />
        <p>{t('chatbotSettings.loading', 'Loading chatbot settings...')}</p>
      </div>
    );
  }

  return (
    <div className="cs-container">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-left">
          <h1 className="cs-title">{t('chatbotSettings.title', 'Chatbot')}</h1>
          {hasUnsavedChanges && (
            <span className="cs-unsaved-badge">
              {t('chatbotSettings.unsaved', 'Unsaved changes')}
            </span>
          )}
        </div>
        <div className="cs-header-actions">
          {selectedChatbotId && (
            <>
              <button className="cs-btn cs-btn-outline" onClick={handleCopyEmbedCode}>
                <FaCode /> {t('chatbotSettings.embedCode', 'Embed Code')}
              </button>
              <button
                className="cs-btn cs-btn-danger-outline"
                onClick={() => handleDelete(selectedChatbotId)}
              >
                <FaTrash /> {t('chatbotSettings.delete', 'Delete')}
              </button>
            </>
          )}
          {user?.has_brand && (
            <button className="cs-btn cs-btn-secondary" onClick={handleCreateNew}>
              <FaPlus /> {t('chatbotSettings.createChatbot', 'New Chatbot')}
            </button>
          )}
          <button
            className="cs-btn cs-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <FaSpinner className="cs-spinner" />
                {t('chatbotSettings.saving', 'Saving...')}
              </>
            ) : (
              <>
                <FaSave /> {t('chatbotSettings.save', 'Save')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="cs-notification cs-notification-error">
          {error}
          <button className="cs-notification-close" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="cs-notification cs-notification-success">
          <FaCheck /> {success}
          <button className="cs-notification-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Multi-brand chatbot selector */}
      {user?.has_brand && chatbotsList.length > 0 && (
        <div className="cs-chatbot-tabs">
          {chatbotsList.map((chatbot) => (
            <button
              key={chatbot.id}
              className={`cs-chatbot-tab ${selectedChatbotId === chatbot.id ? 'active' : ''}`}
              onClick={() => handleSelectChatbot(chatbot)}
            >
              <div className="cs-tab-colors">
                <span className="cs-tab-color-dot" style={{ backgroundColor: chatbot.primary_color }} />
                <span className="cs-tab-color-dot" style={{ backgroundColor: chatbot.secondary_color }} />
              </div>
              <div className="cs-tab-info">
                <span className="cs-tab-name">{chatbot.company_name}</span>
                <span className="cs-tab-brand">{getBrandName(chatbot.brand_id)}</span>
              </div>
              {(chatbot.displayed ?? true) ? (
                <FaEye className="cs-tab-status cs-tab-status-on" />
              ) : (
                <FaEyeSlash className="cs-tab-status cs-tab-status-off" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="cs-main">
        {/* Settings panel */}
        <div className="cs-settings-panel">
          {/* Identity */}
          <div className="cs-section">
            {renderSectionHeader('identity', <FaIdBadge />, t('chatbotSettings.sectionIdentity', 'Identité'))}
            {expandedSections.identity && (
              <div className="cs-section-body">
                {user?.has_brand && (
                  <div className="cs-field">
                    <label htmlFor="brand_id">{t('chatbotSettings.brand', 'Brand')}</label>
                    <select
                      id="brand_id"
                      name="brand_id"
                      value={editSettings.brand_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">{t('chatbotSettings.defaultChatbot', 'Default Chatbot')}</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="cs-field">
                  <label>{t('chatbotSettings.logo', 'Logo')}</label>
                  <div
                    className="cs-logo-drop"
                    role="button"
                    tabIndex={0}
                    onClick={() => logoInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter') logoInputRef.current?.click(); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleLogoFile(e.dataTransfer.files?.[0]); }}
                  >
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="cs-logo-thumb" />
                    ) : (
                      <span className="cs-logo-placeholder">
                        {(editSettings.company_name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="cs-logo-hint">
                      {logoUploading ? (
                        <><FaSpinner className="cs-spinner" /> {t('chatbotSettings.logoUploading', 'Envoi…')}</>
                      ) : (
                        <><FaUpload /> {t('chatbotSettings.logoHint', 'Glissez un logo (PNG, JPEG, WebP) — utilisé dans la bulle, l’en-tête et l’avatar')}</>
                      )}
                    </span>
                    {logoUrl && (
                      <button
                        type="button"
                        className="cs-logo-remove"
                        onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                        aria-label={t('chatbotSettings.logoRemove', 'Retirer le logo')}
                      >
                        <FaTimes />
                      </button>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      hidden
                      onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ''; }}
                    />
                  </div>
                </div>
                <div className="cs-field">
                  <label htmlFor="company_name">{t('chatbotSettings.companyName', 'Company Name')}</label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={editSettings.company_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="agent_name">{t('chatbotSettings.aiAgentName', 'Agent Name')}</label>
                  <input
                    type="text"
                    id="agent_name"
                    name="agent_name"
                    value={editSettings.agent_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="subtitle">{t('chatbotSettings.subtitle', 'Sous-titre')}</label>
                  <input
                    type="text"
                    id="subtitle"
                    name="subtitle"
                    value={editSettings.subtitle}
                    onChange={handleChange}
                    maxLength={60}
                    placeholder={t('chatbotSettings.subtitlePlaceholder', 'répond en quelques secondes')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Theme */}
          <div className="cs-section">
            {renderSectionHeader('theme', <FaPalette />, t('chatbotSettings.sectionTheme', 'Thème'))}
            {expandedSections.theme && (
              <div className="cs-section-body">
                <div className="cs-field">
                  <label>{t('chatbotSettings.brandColor', 'Couleur de marque')}</label>
                  <div className="cs-swatches">
                    {THEME_PRESETS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`cs-swatch${editSettings.primary_color.toUpperCase() === color.toUpperCase() ? ' active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => applyPreset(color)}
                        aria-label={color}
                      />
                    ))}
                    <div className="cs-swatch-custom" title={t('chatbotSettings.customColor', 'Couleur personnalisée')}>
                      <input
                        type="color"
                        name="primary_color"
                        value={editSettings.primary_color}
                        onChange={(e) => applyPreset(e.target.value)}
                        aria-label={t('chatbotSettings.customColor', 'Couleur personnalisée')}
                      />
                    </div>
                  </div>
                  <p className={`cs-contrast ${contrastOk ? 'ok' : 'warn'}`}>
                    {contrastOk
                      ? <><FaCheck /> {t('chatbotSettings.contrastOk', 'Contraste AA vérifié ({{ratio}}:1)', { ratio: ratio ? ratio.toFixed(1) : '—' })}</>
                      : <><FaExclamationTriangle /> {t('chatbotSettings.contrastWarn', 'Contraste faible ({{ratio}}:1) — le texte sera peu lisible', { ratio: ratio ? ratio.toFixed(1) : '—' })}</>}
                  </p>
                </div>
                <details className="cs-advanced">
                  <summary>{t('chatbotSettings.advancedColors', 'Réglages avancés — couleurs séparées')}</summary>
                  <div className="cs-advanced-body">
                    {renderColorField('primary_color', t('chatbotSettings.primaryColor', 'Primary Color'))}
                    {renderColorField('header_color', t('chatbotSettings.headerColor', 'Couleur de l’en-tête'))}
                    {renderColorField('secondary_color', t('chatbotSettings.secondaryColor', 'Secondary Color'))}
                    {renderColorField('tertiary_color', t('chatbotSettings.tertiaryColor', 'Tertiary Color'))}
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Welcome */}
          <div className="cs-section">
            {renderSectionHeader('welcome', <FaCommentDots />, t('chatbotSettings.sectionWelcome', 'Accueil'))}
            {expandedSections.welcome && (
              <div className="cs-section-body">
                <div className="cs-field">
                  <label htmlFor="welcome_title">{t('chatbotSettings.welcomeTitle', 'Titre d’accueil')}</label>
                  <input
                    type="text"
                    id="welcome_title"
                    name="welcome_title"
                    value={editSettings.welcome_title}
                    onChange={handleChange}
                    maxLength={80}
                    placeholder={t('chatbotSettings.welcomeTitlePlaceholder', 'Bonjour, comment pouvons-nous vous aider ?')}
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="greetings_message">{t('chatbotSettings.greetingMessage', 'Greeting Message')}</label>
                  <textarea
                    id="greetings_message"
                    name="greetings_message"
                    value={editSettings.greetings_message}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
                <div className="cs-field">
                  <label>{t('chatbotSettings.suggestedQuestions', 'Questions suggérées ({{n}}/4)', { n: (editSettings.suggested_questions || []).length })}</label>
                  {(editSettings.suggested_questions || []).map((question, index) => (
                    <div className="cs-question-chip" key={`${question}-${index}`}>
                      <span>{question}</span>
                      <button type="button" onClick={() => removeSuggestedQuestion(index)} aria-label={t('chatbotSettings.remove', 'Retirer')}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  {(editSettings.suggested_questions || []).length < 4 && (
                    <div className="cs-question-add">
                      <input
                        type="text"
                        value={questionDraft}
                        onChange={(e) => setQuestionDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuggestedQuestion(); } }}
                        maxLength={80}
                        placeholder={t('chatbotSettings.questionPlaceholder', 'Ex. : Suivre ma commande')}
                      />
                      <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={addSuggestedQuestion}>
                        <FaPlus /> {t('chatbotSettings.add', 'Ajouter')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="cs-field cs-field-toggle">
                  <label htmlFor="teaser_enabled">{t('chatbotSettings.teaserEnabled', 'Message d’invite (bulle fermée)')}</label>
                  <div className="cs-toggle-wrapper">
                    <input
                      type="checkbox"
                      id="teaser_enabled"
                      name="teaser_enabled"
                      checked={!!editSettings.teaser_enabled}
                      onChange={handleChange}
                      className="cs-toggle-input"
                    />
                    <label htmlFor="teaser_enabled" className="cs-toggle-track">
                      <span className="cs-toggle-thumb" />
                    </label>
                  </div>
                </div>
                {editSettings.teaser_enabled && (
                  <>
                    <div className="cs-field">
                      <label htmlFor="teaser_message">{t('chatbotSettings.teaserMessage', 'Texte de l’invite')}</label>
                      <input
                        type="text"
                        id="teaser_message"
                        name="teaser_message"
                        value={editSettings.teaser_message}
                        onChange={handleChange}
                        maxLength={140}
                        placeholder={t('chatbotSettings.teaserPlaceholder', 'Une question sur votre commande ? Je suis là.')}
                      />
                    </div>
                    <div className="cs-field">
                      <label htmlFor="teaser_delay_seconds">
                        {t('chatbotSettings.teaserDelay', 'Délai d’apparition — {{s}} s', { s: editSettings.teaser_delay_seconds })}
                      </label>
                      <input
                        type="range"
                        id="teaser_delay_seconds"
                        name="teaser_delay_seconds"
                        min="1"
                        max="30"
                        value={editSettings.teaser_delay_seconds}
                        onChange={handleChange}
                        className="cs-range"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Bubble */}
          <div className="cs-section">
            {renderSectionHeader('bubble', <FaCircle />, t('chatbotSettings.sectionBubble', 'Bulle & lancement'))}
            {expandedSections.bubble && (
              <div className="cs-section-body">
                <div className="cs-field">
                  <label htmlFor="bubble_shape">{t('chatbotSettings.bubbleShape', 'Shape')}</label>
                  <select
                    id="bubble_shape"
                    name="bubble_shape"
                    value={editSettings.bubble_shape}
                    onChange={handleChange}
                  >
                    <option value="circle">{t('chatbotSettings.circle', 'Circle')}</option>
                    <option value="rounded_square">{t('chatbotSettings.roundedSquare', 'Rounded Square')}</option>
                  </select>
                </div>
                <div className="cs-field">
                  <label htmlFor="bubble_size">
                    {t('chatbotSettings.bubbleSize', 'Size')} — {editSettings.bubble_size}px
                  </label>
                  <input
                    type="range"
                    id="bubble_size"
                    name="bubble_size"
                    min="40"
                    max="80"
                    value={editSettings.bubble_size}
                    onChange={handleChange}
                    className="cs-range"
                  />
                </div>
                <div className="cs-field">
                  <label htmlFor="bubble_position">{t('chatbotSettings.bubblePosition', 'Position')}</label>
                  <select
                    id="bubble_position"
                    name="bubble_position"
                    value={editSettings.bubble_position}
                    onChange={handleChange}
                  >
                    <option value="bottom_right">{t('chatbotSettings.bottomRight', 'Bottom Right')}</option>
                    <option value="bottom_left">{t('chatbotSettings.bottomLeft', 'Bottom Left')}</option>
                  </select>
                </div>
                <div className="cs-color-row">
                  <label htmlFor="bubble_border_color">{t('chatbotSettings.bubbleBorderColor', 'Border Color')}</label>
                  <div className="cs-color-input-group">
                    <input
                      type="color"
                      id="bubble_border_color"
                      name="bubble_border_color"
                      value={editSettings.bubble_border_color || editSettings.primary_color}
                      onChange={handleChange}
                      className="cs-color-picker"
                    />
                    <input
                      type="text"
                      value={editSettings.bubble_border_color || ''}
                      name="bubble_border_color"
                      onChange={handleChange}
                      className="cs-color-hex"
                      placeholder="auto"
                      maxLength={7}
                    />
                  </div>
                </div>
                <div className="cs-field">
                  <label htmlFor="bubble_shadow">{t('chatbotSettings.bubbleShadow', 'Shadow')}</label>
                  <select
                    id="bubble_shadow"
                    name="bubble_shadow"
                    value={editSettings.bubble_shadow}
                    onChange={handleChange}
                  >
                    <option value="none">{t('chatbotSettings.none', 'None')}</option>
                    <option value="default">{t('chatbotSettings.default', 'Default')}</option>
                    <option value="large">{t('chatbotSettings.large', 'Large')}</option>
                  </select>
                </div>
                <p className="cs-field-hint">{t('chatbotSettings.bubbleLogoHint', 'Le logo apparaît automatiquement dans la bulle quand il est défini.')}</p>
              </div>
            )}
          </div>

          {/* Deployment */}
          <div className="cs-section">
            {renderSectionHeader('deploy', <FaRocket />, t('chatbotSettings.sectionDeploy', 'Déploiement'))}
            {expandedSections.deploy && (
              <div className="cs-section-body">
                <div className="cs-field">
                  <label htmlFor="language">{t('chatbotSettings.language', 'Langue des textes système')}</label>
                  <select id="language" name="language" value={editSettings.language} onChange={handleChange}>
                    <option value="fr">{t('chatbotSettings.languageFr', 'Français')}</option>
                    <option value="en">{t('chatbotSettings.languageEn', 'English')}</option>
                  </select>
                </div>
                <div className="cs-field cs-field-toggle">
                  <label htmlFor="displayed">{t('chatbotSettings.displayedLabel', 'Display chatbot')}</label>
                  <div className="cs-toggle-wrapper">
                    <input
                      type="checkbox"
                      id="displayed"
                      name="displayed"
                      checked={!!editSettings.displayed}
                      onChange={handleChange}
                      className="cs-toggle-input"
                    />
                    <label htmlFor="displayed" className="cs-toggle-track">
                      <span className="cs-toggle-thumb" />
                    </label>
                  </div>
                </div>
                <div className="cs-field cs-field-toggle">
                  <label htmlFor="powered_by">{t('chatbotSettings.poweredBy', '« Propulsé par Unanimity »')}</label>
                  <div className="cs-toggle-wrapper">
                    <input
                      type="checkbox"
                      id="powered_by"
                      name="powered_by"
                      checked={!!editSettings.powered_by}
                      onChange={handleChange}
                      className="cs-toggle-input"
                    />
                    <label htmlFor="powered_by" className="cs-toggle-track">
                      <span className="cs-toggle-thumb" />
                    </label>
                  </div>
                </div>
                {showEmbedCode && embedCode && (
                  <div className="cs-field">
                    <p className="cs-embed-hint">
                      {t('chatbotSettings.embedHint', 'Paste this code in your website HTML to display the chatbot.')}
                    </p>
                    <div className="cs-embed-code-block">
                      <code>{embedCode}</code>
                    </div>
                    <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => {
                      navigator.clipboard.writeText(embedCode);
                      setSuccess(t('chatbotSettings.copySuccess', 'Embed code copied to clipboard'));
                      setTimeout(() => setSuccess(null), 3000);
                    }}>
                      <FaCopy /> {t('chatbotSettings.copyEmbedCode', 'Copy')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Live Preview */}
        <div className="cs-preview-panel">
          <div className="cs-preview-modes" role="group" aria-label={t('chatbotSettings.previewModes', 'Aperçu')}>
            {[
              ['home', t('chatbotSettings.previewHome', 'Accueil')],
              ['desktop', t('chatbotSettings.previewDesktop', 'Conversation')],
              ['mobile', t('chatbotSettings.previewMobile', 'Mobile')],
              ['closed', t('chatbotSettings.previewClosed', 'Bulle fermée')],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                className={`cs-preview-mode${previewMode === mode ? ' active' : ''}`}
                aria-pressed={previewMode === mode}
                onClick={() => setPreviewMode(mode)}
              >
                {label}
              </button>
            ))}
          </div>
          <ChatbotPreview
            primary_color={editSettings.primary_color}
            secondary_color={editSettings.secondary_color}
            header_color={editSettings.header_color}
            company_name={editSettings.company_name}
            agent_name={editSettings.agent_name}
            greetings_message={editSettings.greetings_message}
            subtitle={editSettings.subtitle}
            logo_url={logoUrl}
            suggested_questions={editSettings.suggested_questions}
            teaser_enabled={editSettings.teaser_enabled}
            teaser_message={editSettings.teaser_message}
            powered_by={editSettings.powered_by}
            language={editSettings.language}
            bubble_shape={editSettings.bubble_shape}
            bubble_size={editSettings.bubble_size}
            bubble_position={editSettings.bubble_position}
            bubble_border_color={editSettings.bubble_border_color}
            bubble_shadow={editSettings.bubble_shadow}
            welcome_title={editSettings.welcome_title}
            mode={previewMode}
          />
          <p className="cs-preview-note">
            {t('chatbotSettings.previewNote', 'L’aperçu est le rendu réel du widget — essayez d’envoyer un message.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatbotSettings;
